import { useEffect, useState } from 'react';
import { useForecastMap, useMapInteraction } from './ForecastMapContext';
import { getOverlay } from '../../lib/overlays/registry';
import { overlayValueLabel } from '../../lib/overlays/hoverValue';

// Cursor-following readout of the active overlay's value under the pointer,
// replicating Skysight: on mousemove, query the overlay fill feature, take the
// max-idx feature, and map idx -> value (see lib/overlays/hoverValue). Only the
// colored value params are queried (paintOverride params like the cu-cloudbase
// grey "possible cloud" layer carry no legend value).
const OverlayHoverValue = ({ overlayId }) => {
    const map = useForecastMap();
    const { coarsePointer, claimPeek } = useMapInteraction();
    const [tip, setTip] = useState(null); // { x, y, text }

    useEffect(() => {
        if (!map) return undefined;
        const overlay = getOverlay(overlayId);
        if (!overlay) return undefined;
        // Query ALL of the overlay's fill layers, including paintOverride params
        // (cu-cloudbase's grey "possible cloud" `_low` layer carries a cloudbase
        // altitude on the same scale — Skysight reads it on hover too). The
        // reduce below picks the max-idx feature across them, matching Skysight.
        // Ids mirror ForecastOverlay: `ov:{overlay}:{param}:{index}:fill` (the
        // index keeps ids unique when params share one tile source).
        const layerIds = overlay.params
            .map((p, i) => `ov:${overlay.id}:${p.name}:${i}:fill`);

        const compute = (e) => {
            const layers = layerIds.filter((id) => map.getLayer(id));
            if (layers.length === 0) return null;
            const feats = map.queryRenderedFeatures(e.point, { layers });
            if (!feats.length) return null;
            const top = feats.reduce((a, b) =>
                (Number(a.properties.idx) > Number(b.properties.idx) ? a : b));
            const text = overlayValueLabel(overlay, Number(top.properties.idx));
            if (!text) return null;
            return { x: e.point.x, y: e.point.y, text };
        };

        let dismissTimer = null;
        const onMove = (e) => { clearTimeout(dismissTimer); setTip(compute(e)); };
        const onMoveStart = () => setTip(null);
        const onTap = (e) => {
            const r = compute(e);
            setTip(r);
            if (r) {
                claimPeek();
                clearTimeout(dismissTimer);
                dismissTimer = setTimeout(() => setTip(null), 4000);
            }
        };
        const onLeave = () => setTip(null);

        map.on('mousemove', onMove);
        map.on('mouseout', onLeave);
        if (coarsePointer) { map.on('click', onTap); map.on('movestart', onMoveStart); }
        return () => {
            clearTimeout(dismissTimer);
            map.off('mousemove', onMove);
            map.off('mouseout', onLeave);
            if (coarsePointer) { map.off('click', onTap); map.off('movestart', onMoveStart); }
            setTip(null);
        };
    }, [map, overlayId, coarsePointer, claimPeek]);

    if (!tip) return null;
    return (
        <div
            className="pointer-events-none absolute z-[6] px-1.5 py-0.5 rounded bg-slate-900/85 text-white text-[11px] font-medium tabular-nums shadow"
            style={{ left: tip.x, top: tip.y, transform: 'translate(12px, -50%)', textShadow: '0 1px 2px rgba(0, 0, 0, 0.9)' }}
        >
            {tip.text}
        </div>
    );
};

export default OverlayHoverValue;
